import React from 'react'

import { StagePlayer } from './stagePlayer'

// The captions under a scene told in steps (stagePlayer.ts): one per step,
// each a button that jumps to its step, its top rule doubling as the clock.
// On a phone the scene stacks tall and the captions would sit off screen
// while it plays, so they become a strip of segments pinned to the bottom of
// the screen for as long as the scene is above it, with the step on screen
// named under them. The markup is the same at every width; App.css (.Steps)
// draws the two.

interface StepsProps {
	titles: string[]
	///@brief   How long each step stays on screen, which is how long its
	///         bar takes to run along its rule.
	durationsMs: number[]
	player: Pick<StagePlayer, 'stage' | 'bInView' | 'goTo'>
	///@brief   Delay of the fade-in, in seconds, as data-fade-delay takes it.
	fadeDelay: string
}

export function Steps( { titles, durationsMs, player, fadeDelay }: StepsProps ): React.JSX.Element {

	const { stage, bInView, goTo } = player

	return (
		<div className="Steps-Strip" data-fade data-fade-delay={ fadeDelay } >
			<ol className={ `Steps${ bInView ? '' : ' is-paused' }` } style={ { '--steps': titles.length } as React.CSSProperties } >
				{ titles.map( ( title: string, i: number ): React.JSX.Element => {
					const target: number = i + 1
					const bActive: boolean = target === stage
					const bDone: boolean = target < stage
					return (
						<li className="Step" key={ title } >
							<button
								type="button"
								className={ `Step-Button${ bActive ? ' is-active' : '' }${ bDone ? ' is-done' : '' }` }
								style={ { '--duration': `${ durationsMs[ i ] }ms` } as React.CSSProperties }
								aria-current={ bActive ? 'step' : undefined }
								onClick={ (): void => goTo( target ) }
							>
								<span className="Step-Bar" aria-hidden="true" />
								<span className="Step-Index" >0{ target }</span>
								<span className="Step-Title" >{ title }</span>
							</button>
						</li>
					)
				} ) }
			</ol>

			{ /* The step on screen, named once for the strip; the buttons above
			     carry every title, so this says nothing new to a reader. Keyed
			     on the step so its entrance plays again at each change. */ }
			<div className="Steps-Current" aria-hidden="true" >
				<span className="Step-Index" >0{ stage }</span>
				<span className="Step-Title" key={ stage } >{ titles[ stage - 1 ] }</span>
			</div>
		</div>
	)
}
